import type { AttachmentKind, ContextItem } from "@racler/protocol";
import { MAX_CONTEXT_ITEMS_PER_MESSAGE } from "@racler/shared";

export interface EditorSelectionState {
  path: string;
  startLine: number;
  startColumn: number;
  endLine: number;
  endColumn: number;
  selectedText?: string;
}

export interface AtMatch {
  query: string;
  start: number;
  end: number;
}

/**
 * Checks if the text before the cursor ends in an '@' mention query.
 * Must be at start of line or preceded by whitespace.
 */
export function findAtTrigger(text: string, cursorPos: number): AtMatch | null {
  if (cursorPos < 0 || cursorPos > text.length) return null;
  const textBefore = text.slice(0, cursorPos);
  const match = /(?:^|\s)@([^\s]*)$/.exec(textBefore);
  if (!match) return null;

  const query = match[1]!;
  const start = match.index + (match[0].startsWith("@") ? 0 : 1);
  return { query, start, end: cursorPos };
}

/**
 * Replaces the '@query' draft text with empty space or clean remainder after chip insertion.
 */
export function replaceAtMention(text: string, match: AtMatch): { newText: string; cursor: number } {
  const before = text.slice(0, match.start);
  const after = text.slice(match.end);
  const newText = (before + after).replace(/[^\S\r\n]{2,}/g, " ").replace(/^\s+/, "");
  return { newText, cursor: Math.min(before.length, newText.length) };
}

/**
 * Replaces the '@query' text with an inline '@path ' reference at the exact position of the query.
 */
export function insertAtMention(
  text: string,
  match: AtMatch,
  path: string,
): { newText: string; cursor: number } {
  const before = text.slice(0, match.start);
  const after = text.slice(match.end);
  const token = `@${path}`;
  const sep = after.length === 0 || !/^\s/.test(after) ? " " : "";
  const newText = before + token + sep + after;
  return { newText, cursor: before.length + token.length + sep.length };
}

/**
 * True when the text contains an inline '@path' reference for the given path.
 */
export function hasInlineMention(text: string, path: string): boolean {
  const token = `@${path}`;
  let from = 0;
  for (;;) {
    const idx = text.indexOf(token, from);
    if (idx === -1) return false;
    const prev = idx === 0 ? "" : text[idx - 1]!;
    const next = text[idx + token.length];
    const startsOk = prev === "" || /\s/.test(prev);
    const endsOk = next === undefined || /[\s,.;:!?)]/.test(next);
    if (startsOk && endsOk) return true;
    from = idx + 1;
  }
}

/**
 * Keeps only mention-backed file/folder items whose inline '@path' reference is still in the text.
 * Returns the same array when nothing was removed.
 */
export function pruneMentionItems(items: ContextItem[], text: string): ContextItem[] {
  const kept = items.filter((i) => (i.type === "project_file" || i.type === "project_folder" ? hasInlineMention(text, i.path) : false));
  return kept.length === items.length ? items : kept;
}

/**
 * Cyclically navigates suggestions list.
 */
export function navigateSuggestions(current: number, count: number, direction: "up" | "down"): number {
  if (count <= 0) return 0;
  if (direction === "down") {
    return (current + 1) % count;
  }
  return (current - 1 + count) % count;
}

/**
 * Checks if two context items are identical.
 */
export function areContextItemsEqual(a: ContextItem, b: ContextItem): boolean {
  if (a.type !== b.type || a.projectId !== b.projectId) return false;
  if (a.type === "attachment" && b.type === "attachment") {
    return a.attachmentId === b.attachmentId;
  }
  if (a.type !== "attachment" && b.type !== "attachment") {
    if (a.path !== b.path) return false;
    if (a.type === "editor_selection" && b.type === "editor_selection") {
      return (
        a.startLine === b.startLine &&
        a.startColumn === b.startColumn &&
        a.endLine === b.endLine &&
        a.endColumn === b.endColumn
      );
    }
    return true;
  }
  return false;
}

/**
 * Adds a context item with deduplication and limit enforcement.
 */
export function addContextItem(existing: readonly ContextItem[], item: ContextItem): ContextItem[] {
  if (existing.some((e) => areContextItemsEqual(e, item))) {
    return [...existing];
  }
  if (existing.length >= MAX_CONTEXT_ITEMS_PER_MESSAGE) {
    return [...existing];
  }
  return [...existing, item];
}

/**
 * Removes a context item by index.
 */
export function removeContextItem(existing: readonly ContextItem[], index: number): ContextItem[] {
  return existing.filter((_, i) => i !== index);
}

/**
 * Builds a file context item.
 */
export function buildFileContextItem(projectId: string, path: string): ContextItem {
  return {
    type: "project_file",
    projectId,
    path,
  };
}

/**
 * Builds a folder context item.
 */
export function buildFolderContextItem(projectId: string, path: string): ContextItem {
  return {
    type: "project_folder",
    projectId,
    path,
  };
}

/**
 * Builds an editor selection context item from editor selection state.
 */
export function buildSelectionContextItem(projectId: string, sel: EditorSelectionState): ContextItem {
  return {
    type: "editor_selection",
    projectId,
    path: sel.path,
    startLine: sel.startLine,
    startColumn: sel.startColumn,
    endLine: sel.endLine,
    endColumn: sel.endColumn,
    selectedText: sel.selectedText,
  };
}

/**
 * Builds an attachment context item.
 */
export function buildAttachmentContextItem(
  projectId: string,
  att: {
    attachmentId: string;
    name: string;
    mediaType: string;
    size: number;
    kind: AttachmentKind;
  },
): ContextItem {
  return {
    type: "attachment",
    projectId,
    attachmentId: att.attachmentId,
    name: att.name,
    mediaType: att.mediaType,
    size: att.size,
    kind: att.kind,
  };
}


/**
 * Handles clearing context items when project changes.
 */
export function updateContextOnProjectChange(
  prevProjectId: string | undefined,
  currentProjectId: string | undefined,
  existing: ContextItem[],
): ContextItem[] {
  if (prevProjectId !== currentProjectId) {
    return [];
  }
  return existing;
}
